import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Modal,
  SpaceBetween,
  Spinner,
  Tabs,
} from "@amzn/awsui-components-react";
import { OmsProduct, OmsReport } from "../../lusca-search-api/generated-src";
import useOmsReport from "../../hooks/useOmsReport";
import OMSReportFiles from "./OMSReportFiles";
import OMSExportPanel from "./OMSExportPanel";
import OMSRecordFilters from "./OMSRecordFilters";
import { fileSize } from "./omsReportFormatting";
import "./omsReportDownload.scss";

function refreshedAt(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

function ReportSummary({
  report,
  showFiles = true,
}: {
  report: OmsReport;
  showFiles?: boolean;
}) {
  return (
    <dl className="oms-report-download__summary">
      <div>
        <dt>Data refreshed</dt>
        <dd>
          <time dateTime={report.sourceRefreshedAt}>
            {refreshedAt(report.sourceRefreshedAt)}
          </time>
        </dd>
      </div>
      <div>
        <dt>Rows</dt>
        <dd>{report.rowCount.toLocaleString()}</dd>
      </div>
      <div>
        <dt>Total size</dt>
        <dd>{fileSize(report.totalBytes)}</dd>
      </div>
      {showFiles && (
        <div>
          <dt>CSV files</dt>
          <dd>{report.parts.length.toLocaleString()}</dd>
        </div>
      )}
    </dl>
  );
}

function unavailableMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 404) return "No completed scheduled report is available yet.";
  if (status === 403) return "You do not have access to download this report.";
  return "The report is temporarily unavailable. Refresh to try again.";
}

export default function OMSReportDownload({
  product,
}: {
  product: OmsProduct;
}) {
  const query = useOmsReport(product);
  const [snapshot, setSnapshot] = useState<OmsReport | null>(null);
  const productLabel = {
    [OmsProduct.Channels]: "PV Channels",
    [OmsProduct.Addon]: "PV Addon",
    [OmsProduct.Tvod]: "PV TVOD",
  }[product];

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            description="Includes the complete scheduled report for this product. Dashboard filters do not affect this download."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  iconName="refresh"
                  ariaLabel="Refresh scheduled report"
                  loading={query.isFetching}
                  loadingText="Refreshing scheduled report"
                  wrapText={false}
                  onClick={() => {
                    query.refetch().catch(() => undefined);
                  }}
                />
                <Button
                  variant="primary"
                  iconName="download"
                  wrapText={false}
                  ariaHaspopup="dialog"
                  disabled={!query.data || query.isFetching || query.isError}
                  onClick={() => {
                    setSnapshot(query.data || null);
                  }}
                >
                  Download full report
                </Button>
              </SpaceBetween>
            }
          >
            Scheduled report
          </Header>
        }
      >
        <SpaceBetween size="xs">
          {query.isLoading && (
            <div className="oms-report-download__status" role="status">
              <Spinner />
              <span>Loading scheduled report…</span>
            </div>
          )}
          {query.isError && (
            <Alert
              type={
                (query.error as { response?: { status?: number } })?.response
                  ?.status === 404
                  ? "info"
                  : "warning"
              }
              header="Report unavailable"
            >
              {unavailableMessage(query.error)}
            </Alert>
          )}
          {query.data && !query.isError && (
            <ReportSummary report={query.data} />
          )}
          {query.isFetching && !query.isLoading && (
            <div role="status">
              <Box color="text-body-secondary">Refreshing report details…</Box>
            </div>
          )}
        </SpaceBetween>
      </Container>
      <Modal
        visible={snapshot !== null}
        onDismiss={() => setSnapshot(null)}
        header={`Download ${productLabel} report`}
        size="x-large"
        height={800}
        position="top"
        closeAriaLabel="Close report download"
        footer={
          <Box float="right">
            <Button wrapText={false} onClick={() => setSnapshot(null)}>
              Close
            </Button>
          </Box>
        }
      >
        {snapshot && (
          <SpaceBetween size="m">
            <ReportSummary report={snapshot} showFiles={false} />
            {snapshot.rowCount === 0 && (
              <Alert type="info">
                This report is empty. The CSV contains column headers.
              </Alert>
            )}
            <Tabs
              tabs={[
                {
                  id: "files",
                  contentRenderStrategy: "lazy",
                  label: "Full report",
                  content: (
                    <SpaceBetween size="m">
                      <OMSExportPanel
                        product={product}
                        report={snapshot}
                        mode="full"
                      />
                      <Header
                        variant="h3"
                        description="Download individual CSV files if you only need a particular part. Each file includes column headers."
                      >
                        Individual CSV files
                      </Header>
                      <OMSReportFiles product={product} report={snapshot} />
                    </SpaceBetween>
                  ),
                },
                {
                  id: "filtered",
                  contentRenderStrategy: "lazy",
                  label: "Filtered export",
                  content: (
                    <OMSRecordFilters product={product} report={snapshot} />
                  ),
                },
              ]}
            />
          </SpaceBetween>
        )}
      </Modal>
    </>
  );
}
